/**
 * The legal entity that operates Datacrate, shared by every public marketing
 * surface (landing footer, Privacy, Terms) so the name, address and contact
 * details cannot drift between pages.
 *
 * Router-independent by design (plain <a href>, no react-router), like the rest
 * of the marketing surface — see marketingRouterIndependence.test.ts.
 */
import React from 'react';

import { GatewayPowerLockup } from './GatewayPowerLockup';

export const LEGAL_ENTITY = {
  name: 'Gateway Power LLC',
  street: '7800 Forsyth Blvd. Suite 300B',
  locality: 'Clayton, MO 63105',
  email: 'info@gatewaypower.io',
  website: 'https://gatewaypower.io',
  websiteLabel: 'gatewaypower.io',
  copyright: '© 2026 Gateway Power LLC.',
} as const;

/** One-line postal address, e.g. for legal-page contact sections. */
export const LEGAL_ENTITY_ADDRESS = `${LEGAL_ENTITY.street}, ${LEGAL_ENTITY.locality}`;

/** Copyright, postal address, email and website link for the marketing footer. */
export function FooterEntity(): React.JSX.Element {
  return (
    <div className="dc-foot-entity">
      <div>{LEGAL_ENTITY.copyright}</div>
      <address>
        {LEGAL_ENTITY.street} · {LEGAL_ENTITY.locality}
        <br />
        <a href={`mailto:${LEGAL_ENTITY.email}`}>{LEGAL_ENTITY.email}</a>
        {' · '}
        <a href={LEGAL_ENTITY.website} target="_blank" rel="noopener noreferrer">
          {LEGAL_ENTITY.websiteLabel}
        </a>
      </address>
    </div>
  );
}

/**
 * The operating entity's lockup (G mark + GATEWAY POWER wordmark, its current
 * website brand), linked to gatewaypower.io. `className` adds to the shared
 * `dc-gw` class; the header and footer size it through their own rules.
 */
export function GatewayPowerLink({ className }: { className?: string }): React.JSX.Element {
  return (
    <a
      className={className ? `dc-gw ${className}` : 'dc-gw'}
      href={LEGAL_ENTITY.website}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Gateway Power, ${LEGAL_ENTITY.websiteLabel} (opens in a new tab)`}
    >
      <GatewayPowerLockup />
    </a>
  );
}

/** The Gateway Power lockup directly under the Datacrate lockup in every marketing footer (landing, Privacy, Terms). */
export function FooterGatewayMark(): React.JSX.Element {
  return <GatewayPowerLink className="dc-foot-gateway" />;
}
